interface TimeDiff {
  date: Date
  now: Date
  diffSecs: number
  diffMins: number
  diffHours: number
  diffDays: number
}

const parseDate = (date: string | Date): Date => (typeof date === 'string' ? new Date(date) : date)

const getTimeDiff = (date: string | Date): TimeDiff => {
  const d = parseDate(date)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)
  return { date: d, now, diffSecs, diffMins, diffHours, diffDays }
}

export const formatDate = (date: string | Date, locale = 'en-US'): string => {
  const d = parseDate(date)
  return d.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export const formatRelativeTime = (date: string | Date): string => {
  const { date: d, diffSecs, diffMins, diffHours, diffDays } = getTimeDiff(date)

  if (diffSecs < 60) {
    return 'just now'
  }
  if (diffMins < 60) {
    return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
  }
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  }
  if (diffDays < 7) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  }
  return formatDate(d)
}

export const truncate = (str: string, maxLength: number): string => {
  if (str.length <= maxLength) {
    return str
  }
  return `${str.slice(0, maxLength - 3)}...`
}

const isSameDay = (d1: Date, d2: Date): boolean =>
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getDate() === d2.getDate()

const isYesterday = (date: Date, now: Date): boolean => {
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  return isSameDay(date, yesterday)
}

const isWithinWeek = (date: Date, now: Date): boolean => {
  const weekAgo = new Date(now)
  weekAgo.setDate(weekAgo.getDate() - 7)
  return date > weekAgo
}

const isCurrentYear = (date: Date, now: Date): boolean => date.getFullYear() === now.getFullYear()

const formatLocalTime = (date: Date): string =>
  date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

export const formatChatTimestamp = (date: string | Date): string => {
  const { date: d, now, diffSecs, diffMins, diffHours } = getTimeDiff(date)

  if (diffSecs < 60) {
    return 'just now'
  }

  if (diffMins < 60) {
    return `${diffMins} min ago`
  }

  if (isSameDay(d, now)) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  }

  const time = formatLocalTime(d)

  if (isYesterday(d, now)) {
    return `yesterday at ${time}`
  }

  if (isWithinWeek(d, now)) {
    const dayName = d.toLocaleDateString(undefined, { weekday: 'long' })
    return `${dayName} at ${time}`
  }

  if (isCurrentYear(d, now)) {
    const monthDay = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    return `${monthDay} at ${time}`
  }

  const fullDate = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  return `${fullDate} at ${time}`
}

export const formatFullDateTime = (date: string | Date): string => {
  const d = parseDate(date)
  return d.toLocaleString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}
